import { useEffect } from "react";
import { Loader2, Download, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDocumentFile } from "@/hooks/useDocumentFile";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import PdfViewer from "@/components/PdfViewer/PdfViewer";

function parsePageNumber(page: string): number {
    const match = page.match(/#page=(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
}

function parsePageFragment(page: string): string {
    const match = page.match(/page=(\d+)/);
    return match ? `&page=${match[1]}` : '';
}

export const DocumentViewerSheet = () => {
    const { viewingDoc: doc, viewingPage: page, sourceText, side, showTabs, hideOverlay, closeDocument } = useDocumentViewer();
    const { blobUrl, isLoading, error, fetchFile, clearFile } = useDocumentFile();

    useEffect(() => {
        if (doc) fetchFile(doc.id);
    }, [doc?.id]);

    const handleOpenChange = (open: boolean) => {
        if (!open) { clearFile(); closeDocument(); }
    };

    const isPDF = /\.pdf/i.test(doc?.filename ?? '');

    const renderLoadingOrError = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading document…
                </div>
            );
        }
        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-sm text-muted-foreground px-8 text-center">
                    <FileText className="w-12 h-12 text-muted-foreground/30" />
                    <p className="font-medium">Could not load document preview</p>
                    <p className="text-xs text-destructive">{error}</p>
                    {doc && (
                        <button type="button" onClick={() => fetchFile(doc.id)} className="text-sm text-brand-teal hover:underline mt-1">
                            Try again
                        </button>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <Sheet open={!!doc} onOpenChange={handleOpenChange} modal={!hideOverlay}>
            <SheetContent side={side} hideOverlay={hideOverlay} onInteractOutside={hideOverlay ? (e) => e.preventDefault() : undefined} className="w-full md:w-[60svw] sm:max-w-none flex flex-col p-0 [&>button]:text-white">
                <Tabs defaultValue="references" className="flex flex-col h-full overflow-hidden">
                    <SheetHeader className="px-3 py-6 bg-primary text-white shrink-0">
                        <div className="flex items-start justify-between gap-4 pr-8">
                            <div className="min-w-0 flex-1">
                                <SheetTitle className="text-white leading-snug">
                                    {doc?.title || doc?.filename}
                                </SheetTitle>
                                <SheetDescription asChild>
                                    <div className="text-xs truncate">
                                        {!isPDF && doc?.filename && <span className="block text-white/70">{doc.filename}</span>}
                                        <div className="flex flex-wrap items-center gap-2 text-white mt-1">
                                            {doc?.author && <span className="text-xs">{doc.author}</span>}
                                            {doc?.year && <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-black">{doc.year}</span>}
                                            {doc?.sourceType && <span className="text-xs px-1.5 py-0.5 rounded bg-brand-teal text-white">{doc.sourceType}</span>}
                                        </div>
                                    </div>
                                </SheetDescription>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {showTabs && isPDF && (
                                <TabsList className="w-fit">
                                    <TabsTrigger value="references">References</TabsTrigger>
                                    <TabsTrigger value="document">Document</TabsTrigger>
                                </TabsList>
                            )}
                            {blobUrl && (
                                <a
                                    href={blobUrl}
                                    download={doc?.filename || "document.pdf"}
                                    className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border hover:bg-muted hover:text-black transition-colors font-medium ml-auto"
                                >
                                    <Download className="w-4 h-4" />
                                    Download
                                </a>
                            )}
                        </div>
                    </SheetHeader>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        {isLoading || error ? renderLoadingOrError() : (
                            <>
                                {showTabs && isPDF ? (
                                    <>
                                        <TabsContent value="references" className="flex-1 overflow-hidden mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                                            {blobUrl && (
                                                <PdfViewer
                                                    key={blobUrl}
                                                    pdfUrl={blobUrl}
                                                    initialPage={parsePageNumber(page)}
                                                    citation={{ sourceText }}
                                                    fileName={doc?.filename ?? 'document.pdf'}
                                                />
                                            )}
                                        </TabsContent>
                                        <TabsContent value="document" className="flex-1 overflow-hidden mt-0 data-[state=active]:flex data-[state=active]:flex-col">
                                            {blobUrl && (
                                                <iframe
                                                    src={`${blobUrl}#toolbar=1&navpanes=0${parsePageFragment(page)}`}
                                                    className="w-full flex-1 border-0"
                                                    title={doc?.title ?? "Document Preview"}
                                                />
                                            )}
                                        </TabsContent>
                                    </>
                                ) : blobUrl && (
                                    <iframe
                                        src={`${blobUrl}#toolbar=1&navpanes=0${parsePageFragment(page)}`}
                                        className="w-full flex-1 border-0"
                                        title={doc?.title ?? "Document Preview"}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Tabs>
            </SheetContent>
        </Sheet>
    );
};
